// Unpublished Work © 2020-2024 Deere & Company.

import React from 'react';
import {isEqual} from 'lodash';

function fetchEffectData(fetchCallback, returnCallback) {
    let mounted = true;

    fetchCallback(() => mounted);

    return () => {
        mounted = false;

        if (returnCallback) {
            returnCallback();
        }
    };
}

function isAbortError(e) {
    const name = e?.name;
    const code = e?.code;
    const message = typeof e?.message === 'string' ? e.message.toLowerCase() : '';

    return name === 'AbortError' ||
        name === 'CanceledError' ||
        code === 'ERR_CANCELED' ||
        message === 'canceled' ||
        message === 'cancelled' ||
        message.includes('aborted');
}

function fetchEffectDataV2(fetchCallback, options = {}) {
    const {
        onError,
        onFinally,
        onCleanup,
        controller: injectedController
    } = options;

    const controller = injectedController ?? new AbortController();

    let active = true;

    const isActive = () => active && !controller.signal.aborted;

    const run = async () => {
        try {
            await fetchCallback({
                signal: controller.signal,
                isActive
            });
        } catch (e) {
            if (!isAbortError(e) && typeof onError === 'function') {
                onError(e);
            }
        } finally {
            if (typeof onFinally === 'function') {
                onFinally();
            }
        }
    };

    run();

    return () => {
        active = false;
        controller.abort();
        if (typeof onCleanup === 'function') {
            onCleanup();
        }
    };
}

function memoizeDeepCompare(deps) {
    const ref = React.useRef();

    if (!isEqual(deps, ref.current)) {
        ref.current = deps;
    }

    return ref.current;
}

function useDeepEffect(effect, deps) {
    React.useEffect(effect, memoizeDeepCompare(deps));
}

function useDeepMemo(factory, deps) {
    const ref = React.useRef();

    if (!ref.current || !isEqual(deps, ref.current.key)) {
        ref.current = {
            key: deps,
            value: factory()
        };
    }

    return ref.current.value;
}

function useLazyRef(initialValue) {
    const initialized = React.useRef(false);
    const ref = React.useRef();

    if (!initialized.current) {
        initialized.current = true;
        ref.current = initialValue();
    }

    return ref;
}

export {
    fetchEffectData,
    useDeepEffect,
    useDeepMemo,
    useLazyRef,
    fetchEffectDataV2,
    isAbortError
};
